import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import moment from 'moment'
import UserPagination from '../common/UserPagination'
import {
  getRfidInventorySession,
  getRfidInventorySessions,
} from '../../modules/Rfid'
import { getErrorString } from '../../utils/errorUtils'

type Props = {
  group: IGroup
}

const SESSION_STATUS_LABELS: Record<string, string> = {
  in_progress: '棚卸中',
  completed: '完了',
  cancelled: '中止',
}

const RESULT_STATUS_LABELS: Record<string, string> = {
  matched: '一致',
  discrepancy: '差異あり',
}

const ITEM_STATUS_LABELS: Record<string, string> = {
  pending: '未確認',
  found: '確認済み',
  missing: '未検出',
  unexpected: '未登録',
}

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-'
  return moment(value).utcOffset(540).format('YYYY/MM/DD HH:mm:ss')
}

const formatUser = (session: IRfidInventorySession): string => {
  const user = session.performed_by_user
  if (user) return user.name || user.email || `ID: ${user.id}`
  return session.performed_by_user_id ? `ID: ${session.performed_by_user_id}` : '-'
}

const formatReaderDevice = (session: IRfidInventorySession, group: IGroup): string => {
  if (!session.reader_device_id) return '-'
  const device = (group.devices ?? []).find(
    (candidate) => candidate.id === session.reader_device_id,
  )
  return device?.name || `ID: ${session.reader_device_id}`
}

const formatLocation = (session: IRfidInventorySession): string => {
  if (session.latitude === null || session.latitude === undefined
    || session.longitude === null || session.longitude === undefined) {
    return '-'
  }

  const latitude = Number(session.latitude)
  const longitude = Number(session.longitude)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return '-'

  const accuracy = Number(session.location_accuracy)
  const accuracyLabel = Number.isFinite(accuracy) ? `（精度 ${accuracy}m）` : ''
  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}${accuracyLabel}`
}

const statusClassName = (status: string): string => (
  `rfid-inventory-status rfid-inventory-status--${status}`
)

const RfidInventoryHistory = ({ group }: Props) => {
  const [sessions, setSessions] = useState<IRfidInventorySession[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null)
  const [selectedSession, setSelectedSession] = useState<IRfidInventorySession | null>(null)
  const [loading, setLoading] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [detailError, setDetailError] = useState<string | null>(null)
  const listRequestIdRef = useRef(0)
  const detailRequestIdRef = useRef(0)

  const loadSessions = useCallback(async () => {
    const requestId = listRequestIdRef.current + 1
    listRequestIdRef.current = requestId
    setLoading(true)
    setError(null)

    try {
      const result = await getRfidInventorySessions(group.id, {
        page: currentPage,
        perPage: 50,
      })
      if (listRequestIdRef.current !== requestId) return

      setSessions(result.sessions)
      setCurrentPage(result.currentPage)
      setTotalPages(Math.max(result.totalPages, 1))
      setTotalCount(result.totalCount)
    } catch (loadError) {
      if (listRequestIdRef.current !== requestId) return

      setSessions([])
      setError(getErrorString(loadError))
    } finally {
      if (listRequestIdRef.current === requestId) setLoading(false)
    }
  }, [currentPage, group.id])

  const loadDetail = useCallback(async () => {
    if (!selectedSessionId) return

    const requestId = detailRequestIdRef.current + 1
    detailRequestIdRef.current = requestId
    setDetailLoading(true)
    setDetailError(null)

    try {
      const detail = await getRfidInventorySession(group.id, selectedSessionId)
      if (detailRequestIdRef.current !== requestId) return
      setSelectedSession(detail)
    } catch (loadError) {
      if (detailRequestIdRef.current !== requestId) return
      setSelectedSession(null)
      setDetailError(getErrorString(loadError))
    } finally {
      if (detailRequestIdRef.current === requestId) setDetailLoading(false)
    }
  }, [group.id, selectedSessionId])

  useEffect(() => {
    loadSessions()
  }, [loadSessions])

  useEffect(() => {
    loadDetail()
  }, [loadDetail])

  useEffect(() => () => {
    listRequestIdRef.current += 1
    detailRequestIdRef.current += 1
  }, [])

  const openDetail = (sessionId: number) => {
    setSelectedSessionId(sessionId)
    setSelectedSession(null)
    setDetailError(null)
  }

  const closeDetail = () => {
    detailRequestIdRef.current += 1
    setSelectedSessionId(null)
    setSelectedSession(null)
    setDetailError(null)
  }

  const items = useMemo(
    () => selectedSession?.items ?? [],
    [selectedSession],
  )

  if (selectedSessionId) {
    return (
      <section className="rfid-panel" data-testid="rfid-inventory-history-detail">
        <div className="rfid-panel__header">
          <div>
            <h3>{`棚卸記録 #${selectedSessionId}`}</h3>
            <p className="rfid-panel__description">
              棚卸開始時点のRFID名と識別子で結果を表示します。
            </p>
          </div>
          <button type="button" className="btn btn-outline-secondary" onClick={closeDetail}>
            一覧に戻る
          </button>
        </div>

        {detailLoading && <div className="rfid-message">棚卸記録を読み込んでいます...</div>}
        {detailError && (
          <div className="rfid-message rfid-message--error">
            {detailError}
            {' '}
            <button type="button" className="btn btn-link" onClick={loadDetail}>
              再読み込み
            </button>
          </div>
        )}

        {!detailLoading && !detailError && selectedSession && (
          <>
            <dl className="rfid-inventory-detail-summary">
              <div>
                <dt>状態</dt>
                <dd>
                  <span className={statusClassName(selectedSession.status)}>
                    {SESSION_STATUS_LABELS[selectedSession.status] ?? selectedSession.status}
                  </span>
                </dd>
              </div>
              <div>
                <dt>照合結果</dt>
                <dd>
                  {selectedSession.result_status
                    ? RESULT_STATUS_LABELS[selectedSession.result_status]
                      ?? selectedSession.result_status
                    : '-'}
                </dd>
              </div>
              <div>
                <dt>開始日時</dt>
                <dd>{formatDateTime(selectedSession.started_at)}</dd>
              </div>
              <div>
                <dt>終了日時</dt>
                <dd>{formatDateTime(selectedSession.finished_at)}</dd>
              </div>
              <div>
                <dt>実施者</dt>
                <dd>{formatUser(selectedSession)}</dd>
              </div>
              <div>
                <dt>読取Device</dt>
                <dd>{formatReaderDevice(selectedSession, group)}</dd>
              </div>
              <div>
                <dt>位置情報</dt>
                <dd>{formatLocation(selectedSession)}</dd>
              </div>
              <div>
                <dt>メモ</dt>
                <dd>{selectedSession.notes || '-'}</dd>
              </div>
            </dl>

            <div className="rfid-inventory-summary" data-testid="rfid-inventory-history-summary">
              <div>
                <strong>{selectedSession.expected_count}</strong>
                <span>対象</span>
              </div>
              <div>
                <strong>{selectedSession.found_count}</strong>
                <span>確認済み</span>
              </div>
              <div>
                <strong>{selectedSession.missing_count}</strong>
                <span>未検出</span>
              </div>
              <div>
                <strong>{selectedSession.unexpected_count}</strong>
                <span>未登録</span>
              </div>
            </div>

            <div className="rfid-table-wrapper">
              <table className="table" data-testid="rfid-inventory-history-items">
                <thead>
                  <tr>
                    <th>状態</th>
                    <th>RFID名</th>
                    <th>識別子</th>
                    <th>読取回数</th>
                    <th>初回読取</th>
                    <th>最終読取</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="rfid-table-empty">
                        棚卸対象の明細がありません。
                      </td>
                    </tr>
                  ) : items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <span className={statusClassName(item.status)}>
                          {ITEM_STATUS_LABELS[item.status] ?? item.status}
                        </span>
                      </td>
                      <td>{item.name_snapshot || '-'}</td>
                      <td className="rfid-code">{item.identifier_snapshot}</td>
                      <td>{item.scan_count}</td>
                      <td>{formatDateTime(item.first_seen_at)}</td>
                      <td>{formatDateTime(item.last_seen_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    )
  }

  return (
    <section className="rfid-panel" data-testid="rfid-inventory-history">
      <div className="rfid-panel__header">
        <div>
          <h3>RFID棚卸記録</h3>
          <p className="rfid-panel__description">
            完了・中止・実施中を含む棚卸記録を新しい順に表示します。
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary"
          disabled={loading}
          onClick={loadSessions}
        >
          再読み込み
        </button>
      </div>

      {loading && <div className="rfid-message">棚卸記録を読み込んでいます...</div>}
      {error && (
        <div className="rfid-message rfid-message--error">{error}</div>
      )}

      {!loading && !error && (
        <>
          <p className="rfid-inventory-history-count">
            {totalCount}
            件
          </p>
          <div className="rfid-table-wrapper">
            <table className="table" data-testid="rfid-inventory-history-list">
              <thead>
                <tr>
                  <th>開始日時</th>
                  <th>状態</th>
                  <th>照合結果</th>
                  <th>実施者</th>
                  <th>対象</th>
                  <th>確認済み</th>
                  <th>未検出</th>
                  <th>未登録</th>
                  <th aria-label="操作" />
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="rfid-table-empty">
                      棚卸記録はありません。
                    </td>
                  </tr>
                ) : sessions.map((session) => (
                  <tr key={session.id}>
                    <td>{formatDateTime(session.started_at)}</td>
                    <td>
                      <span className={statusClassName(session.status)}>
                        {SESSION_STATUS_LABELS[session.status] ?? session.status}
                      </span>
                    </td>
                    <td>
                      {session.result_status
                        ? RESULT_STATUS_LABELS[session.result_status] ?? session.result_status
                        : '-'}
                    </td>
                    <td>{formatUser(session)}</td>
                    <td>{session.expected_count}</td>
                    <td>{session.found_count}</td>
                    <td>{session.missing_count}</td>
                    <td>{session.unexpected_count}</td>
                    <td className="rfid-table-actions">
                      <button
                        type="button"
                        className="btn btn-link"
                        onClick={() => openDetail(session.id)}
                      >
                        詳細を見る
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <UserPagination
              currentPageNumber={currentPage}
              totalPages={totalPages}
              onChangePage={setCurrentPage}
            />
          )}
        </>
      )}
    </section>
  )
}

export default RfidInventoryHistory
